// client/src/pages/secure/EmployeeListPage.js
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom'; // Import Link
import { useApiClient } from '../../hooks/useApiClient';
import DataList from '../../components/shared/DataList';
import { getCurrentUser, canManageStaff } from '../../hooks/useCurrentUser';

// --- Add Employee Modal Component ---
const AddEmployeeModal = ({ isOpen, onClose, onEmployeeAdded }) => {
    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        jobTitle: '',
        email: '',
        phone: '',
        status: 'Active',
    });
    const [error, setError] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const apiClient = useApiClient();

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setIsSubmitting(true);
        try {
            const newEmployee = await apiClient.post('/api/employees', formData);
            onEmployeeAdded(newEmployee);
            onClose();
        } catch (err) {
            setError(err.message || 'Failed to create employee.');
        } finally {
            setIsSubmitting(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center">
            <div className="bg-white p-8 rounded-lg shadow-xl w-full max-w-lg">
                <h2 className="text-2xl font-bold text-slate-800 mb-6">Add New Employee</h2>
                <form onSubmit={handleSubmit}>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <input name="firstName" value={formData.firstName} onChange={handleChange} placeholder="First Name" required className="p-2 border rounded" />
                        <input name="lastName" value={formData.lastName} onChange={handleChange} placeholder="Last Name" required className="p-2 border rounded" />
                        <input name="jobTitle" value={formData.jobTitle} onChange={handleChange} placeholder="Job Title" required className="p-2 border rounded" />
                        <input name="email" type="email" value={formData.email} onChange={handleChange} placeholder="Email Address" className="p-2 border rounded" />
                        <input name="phone" value={formData.phone} onChange={handleChange} placeholder="Phone Number" className="p-2 border rounded" />
                        <select name="status" value={formData.status} onChange={handleChange} className="p-2 border rounded">
                            <option value="Active">Active</option>
                            <option value="On Leave">On Leave</option>
                            <option value="Terminated">Terminated</option>
                        </select>
                    </div>
                    {error && <p className="text-red-500 text-sm mt-4">{error}</p>}
                    <div className="flex justify-end gap-4 mt-6">
                        <button type="button" onClick={onClose} className="px-4 py-2 bg-gray-200 rounded">Cancel</button>
                        <button type="submit" disabled={isSubmitting} className="px-4 py-2 bg-slate-800 text-white rounded disabled:bg-indigo-400">
                            {isSubmitting ? 'Saving...' : 'Save Employee'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};


// --- Employee List Page Component ---
const EmployeeListPage = () => {
    const [employees, setEmployees] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const apiClient = useApiClient();
    // Job access (26 Oct 2026): the managing broker sees each employee's job profile here too.
    const isMB = !!getCurrentUser()?.isManagingBroker;
    const [jobByEmployee, setJobByEmployee] = useState(null);
    useEffect(() => {
        if (!isMB) return;
        apiClient.get('/api/job-access/people')
            .then((list) => setJobByEmployee(new Map((list || []).filter((p) => p.claims?.employeeId).map((p) => [p.claims.employeeId, p]))))
            .catch(() => setJobByEmployee(null));
    }, [apiClient, isMB]);

    useEffect(() => {
        apiClient.get('/api/employees')
            .then(data => setEmployees(data))
            .catch(err => setError(err.message || 'Failed to fetch employees.'))
            .finally(() => setIsLoading(false));
    }, [apiClient]);

    const handleEmployeeAdded = (newEmployee) => {
        setEmployees(prev => [newEmployee, ...prev]);
    };

    return (
        <>
            <AddEmployeeModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                onEmployeeAdded={handleEmployeeAdded}
            />
            <div className="bg-white p-8 rounded-lg shadow">
                <div className="flex justify-between items-center mb-6">
                    <h1 className="text-2xl font-bold text-slate-800">Employee Management</h1>
                    {canManageStaff(getCurrentUser()) && (
                        <button
                            onClick={() => setIsModalOpen(true)}
                            className="px-4 py-2 text-sm font-medium text-white bg-slate-800 rounded-md hover:bg-slate-900 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-slate-500"
                        >
                            Add New Employee
                        </button>
                    )}
                </div>

                {isLoading && <p>Loading employees...</p>}
                {error && <p className="text-red-500">{error}</p>}
                {!isLoading && !error && (
                    <DataList rows={employees} rowKey="employee_id" csvName="employees" defaultSort={{ key: 'name', dir: 'asc' }}
                        filters={[{ key: 'status', label: 'Status' }, { key: 'staff_role', label: 'Access' }]}
                        columns={[
                            { key: 'name', label: 'Name', value: (e) => `${e.first_name} ${e.last_name}`, render: (e) => <Link to={`/dashboard/employees/${e.employee_id}`} className="text-indigo-700 font-medium">{e.first_name} {e.last_name}</Link> },
                            { key: 'job_title', label: 'Job title' },
                            { key: 'staff_role', label: 'Access' },
                            { key: 'email', label: 'Email' },
                            { key: 'status', label: 'Status' },
                            ...(jobByEmployee ? [{ key: 'job_access', label: 'Job access', search: false,
                                value: (e) => { const p = jobByEmployee.get(e.employee_id); return p ? (p.is_managing_broker ? 'everything' : (p.profile_key || 'as access level')) : 'no login'; },
                                render: (e) => { const p = jobByEmployee.get(e.employee_id);
                                    if (!p) return <span className="text-xs text-slate-400">no login</span>;
                                    if (p.is_managing_broker) return <span className="text-xs text-slate-400">everything</span>;
                                    return <Link to={`/dashboard/job-access?user=${p.user_id}`} className="text-indigo-600 hover:underline">{p.profile_key ? p.profile_key.replace(/_/g, ' ') : 'as access level'}</Link>; } }] : []),
                            { key: 'actions', label: '', sortable: false, csv: false, search: false, render: (e) => <Link to={`/dashboard/employees/${e.employee_id}`} className="text-indigo-600 hover:text-indigo-900">Manage</Link> },
                        ]} />
                )}
            </div>
        </>
    );
};

export default EmployeeListPage;
